// N8N Webhook Service
// Handles communication with N8N workflows for email processing

import { getEmailConnections } from '@/lib/database/email-connections-helpers';

class N8NWebhookService {
  constructor() {
    this.webhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL || 'https://prismdigitallabs.app.n8n.cloud/webhook/extract-camp-emails';
    this.webhookSecret = import.meta.env.VITE_N8N_WEBHOOK_SECRET;
    this.webhookToken = import.meta.env.VITE_N8N_WEBHOOK_TOKEN;
    this.timeout = parseInt(import.meta.env.VITE_WEBHOOK_TIMEOUT) || 30000;
    this.retryAttempts = 3;
    this.rateLimitDelay = parseInt(import.meta.env.VITE_RATE_LIMIT_DELAY) || 1000; // 1 second default
    this.maxRetries = 3;
    this.retryDelay = 2000; // 2 seconds between retries
    this.requestQueue = [];
    this.isProcessing = false;
  }

  /**
   * Send email data to N8N webhook for processing
   * @param {Object} emailData - Email data to process
   * @returns {Promise<Object>} Processing result
   */
  async processEmail(emailData) {
    if (!this.webhookUrl) {
      throw new Error('N8N webhook URL not configured');
    }

    const payload = this.buildWebhookPayload(emailData);
    
    // Add rate limiting delay
    await this.rateLimitDelay;
    
    let lastError;
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        console.log(`🔄 Processing email ${emailData.messageId} (attempt ${attempt}/${this.maxRetries})`);
        const response = await this.sendWebhookRequest(payload);
        return this.handleWebhookResponse(response);
      } catch (error) {
        lastError = error;
        console.error(`❌ Error processing email with N8N (attempt ${attempt}):`, error);
        
        // Check if it's a rate limit error
        if (this.isRateLimitError(error)) {
          const delay = this.calculateBackoffDelay(attempt);
          console.log(`⏳ Rate limit detected, waiting ${delay}ms before retry...`);
          await this.sleep(delay);
          continue;
        }
        
        // For other errors, wait before retry
        if (attempt < this.maxRetries) {
          console.log(`⏳ Waiting ${this.retryDelay}ms before retry...`);
          await this.sleep(this.retryDelay);
        }
      }
    }
    
    throw new Error(`Failed to process email after ${this.maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
  }

  /**
   * Check if error is a rate limit error
   * @param {Error} error - Error object
   * @returns {boolean} True if rate limit error
   */
  isRateLimitError(error) {
    if (!error) return false;
    
    const errorMessage = error.message.toLowerCase();
    const errorStatus = error.status || error.statusCode;
    
    // Check for common rate limit indicators
    return (
      errorStatus === 429 ||
      errorMessage.includes('rate limit') ||
      errorMessage.includes('too many requests') ||
      errorMessage.includes('quota exceeded') ||
      errorMessage.includes('rate limit exceeded')
    );
  }

  /**
   * Calculate exponential backoff delay
   * @param {number} attempt - Current attempt number
   * @returns {number} Delay in milliseconds
   */
  calculateBackoffDelay(attempt) {
    const baseDelay = 1000; // 1 second base
    const maxDelay = 60000; // 60 seconds max
    const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
    return delay;
  }

  /**
   * Sleep for specified milliseconds
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Build webhook payload from email data with comprehensive validation
   * @param {Object} emailData - Email data
   * @returns {Object} Webhook payload
   */
  buildWebhookPayload(emailData) {
    // Validate email data structure
    const validationResult = this.validateEmailData(emailData);
    if (!validationResult.isValid) {
      throw new Error(`Invalid email data: ${validationResult.errors.join(', ')}`);
    }

    // Determine if this is a test or production request
    const isTest = this.determineTestMode(emailData);
    
    // Build payload based on request type
    if (isTest) {
      return this.buildTestPayload(emailData);
    } else {
      return this.buildProductionPayload(emailData);
    }
  }

  /**
   * Determine if request should be treated as test mode
   * @param {Object} emailData - Email data
   * @returns {boolean} True if test mode
   */
  determineTestMode(emailData) {
    // Check for explicit test indicators
    if (emailData.processing_type === 'test') {
      console.log('🧪 Test mode detected: processing_type = "test"');
      return true;
    }
    
    if (emailData.metadata?.isTest === true) {
      console.log('🧪 Test mode detected: metadata.isTest = true');
      return true;
    }
    
    // Check for test data indicators
    const testIndicators = [
      'test',
      'debug',
      'sample',
      'mock',
      'fake'
    ];
    
    const messageId = emailData.messageId?.toLowerCase() || '';
    const userId = emailData.userId?.toLowerCase() || '';
    const sender = emailData.sender?.toLowerCase() || '';
    const subject = emailData.subject?.toLowerCase() || '';
    
    for (const indicator of testIndicators) {
      if (messageId.includes(indicator) || 
          userId.includes(indicator) || 
          sender.includes(indicator) || 
          subject.includes(indicator)) {
        console.log(`🧪 Test mode detected: "${indicator}" found in data`);
        return true;
      }
    }
    
    // Check for test email domains
    const testDomains = [
      'example.com',
      'test.com',
      'localhost',
      'mock.com',
      'fake.com'
    ];
    
    for (const domain of testDomains) {
      if (sender.includes(domain)) {
        console.log(`🧪 Test mode detected: test domain "${domain}" in sender`);
        return true;
      }
    }
    
    console.log('🚀 Production mode: No test indicators found');
    return false;
  }

  /**
   * Build test payload that bypasses Gmail API calls
   * @param {Object} emailData - Email data
   * @returns {Object} Test webhook payload
   */
  buildTestPayload(emailData) {
    console.log('🧪 Building test payload (bypassing Gmail API calls)');
    
    return {
      // Core identification
      userId: emailData.userId,
      messageId: emailData.messageId,
      processing_type: 'test',
      
      // Email data (can be mock/test data)
      subject: emailData.subject || 'Test Email Subject',
      sender: emailData.sender || 'test@example.com',
      content: emailData.content || 'Test email content for processing',
      rawMessage: emailData.rawMessage || 'Test raw message content',
      
      // Test mode indicators
      test_mode: true,
      bypass_gmail_api: true,
      
      // Timestamp
      timestamp: new Date().toISOString(),
      
      // Metadata
      metadata: {
        source: 'camp-connect',
        version: '1.0.0',
        isTest: true,
        testType: 'webhook-integration',
        bypassReasons: [
          'processing_type=test',
          'Gmail API calls disabled in test mode',
          'Using mock data for testing'
        ],
        ...emailData.metadata
      }
    };
  }

  /**
   * Build production payload with real user data
   * @param {Object} emailData - Email data
   * @returns {Object} Production webhook payload
   */
  buildProductionPayload(emailData) {
    console.log('🚀 Building production payload with real user data');
    
    // Validate required production fields
    const productionValidation = this.validateProductionData(emailData);
    if (!productionValidation.isValid) {
      throw new Error(`Production data validation failed: ${productionValidation.errors.join(', ')}`);
    }
    
    return {
      // REQUIRED FIELDS - All four fields must be present for N8N workflow
      user_id: emailData.userId, // Must be a valid UUID from authenticated user
      email_address: emailData.email_address || emailData.sender, // User's email address
      access_token: emailData.access_token, // Gmail OAuth token (required for Gmail API calls)
      processing_type: emailData.processing_type || 'historical', // Used for workflow logic
      
      // Additional identification
      message_id: emailData.messageId,
      
      // Real Gmail data
      refresh_token: emailData.refresh_token,
      
      // Email content
      subject: emailData.subject,
      sender: emailData.sender,
      content: emailData.content,
      raw_message: emailData.rawMessage,
      
      // Gmail-specific data
      gmail_message_id: emailData.gmail_message_id || emailData.messageId,
      gmail_thread_id: emailData.gmail_thread_id,
      gmail_labels: emailData.gmail_labels || [],
      
      // Production mode indicators
      test_mode: false,
      bypass_gmail_api: false,
      
      // Timestamp
      timestamp: new Date().toISOString(),
      
      // Metadata
      metadata: {
        source: 'camp-connect',
        version: '1.0.0',
        isTest: false,
        environment: import.meta.env.MODE || 'development',
        processing_origin: 'production_webhook',
        ...emailData.metadata
      }
    };
  }

  /**
   * Validate email data structure
   * @param {Object} emailData - Email data to validate
   * @returns {Object} Validation result with isValid boolean and errors array
   */
  validateEmailData(emailData) {
    const errors = [];
    
    // Check if emailData exists
    if (!emailData || typeof emailData !== 'object') {
      errors.push('Email data is required and must be an object');
      return { isValid: false, errors };
    }

    // Basic required fields (relaxed for test data)
    const basicRequiredFields = ['userId', 'messageId'];
    basicRequiredFields.forEach(field => {
      if (!emailData[field]) {
        errors.push(`${field} is required`);
      }
    });

    // Validate field types
    if (emailData.userId && typeof emailData.userId !== 'string') {
      errors.push('userId must be a string');
    }
    if (emailData.messageId && typeof emailData.messageId !== 'string') {
      errors.push('messageId must be a string');
    }
    if (emailData.subject && typeof emailData.subject !== 'string') {
      errors.push('subject must be a string');
    }
    if (emailData.sender && typeof emailData.sender !== 'string') {
      errors.push('sender must be a string');
    }
    if (emailData.content && typeof emailData.content !== 'string') {
      errors.push('content must be a string');
    }

    // Validate email format for sender (only if provided)
    if (emailData.sender && !this.isValidEmail(emailData.sender)) {
      errors.push('sender must be a valid email address');
    }

    // Check for test data indicators
    const isTestData = this.determineTestMode(emailData);

    // If it's test data, be more lenient with validation
    if (isTestData) {
      console.log('🧪 Detected test data - using relaxed validation');
      // Remove some validation errors for test data
      const testErrors = errors.filter(error => 
        !error.includes('userId is required') && 
        !error.includes('messageId is required')
      );
      return { isValid: testErrors.length === 0, errors: testErrors, isTest: true };
    }

    return { isValid: errors.length === 0, errors, isTest: false };
  }

  /**
   * Validate production data requirements
   * @param {Object} emailData - Email data to validate for production
   * @returns {Object} Validation result with isValid boolean and errors array
   */
  validateProductionData(emailData) {
    const errors = [];
    
    console.log('🔍 Validating production data requirements...');
    
    // Required fields for production
    const requiredFields = [
      'userId',
      'messageId', 
      'subject',
      'sender',
      'content',
      'access_token'
    ];
    
    requiredFields.forEach(field => {
      if (!emailData[field]) {
        errors.push(`${field} is required for production requests`);
      }
    });

    // Validate access token format
    if (emailData.access_token) {
      if (typeof emailData.access_token !== 'string') {
        errors.push('access_token must be a string');
      } else if (emailData.access_token.length < 10) {
        errors.push('access_token appears to be invalid (too short)');
      } else if (emailData.access_token.includes('test') || emailData.access_token.includes('mock')) {
        errors.push('access_token appears to be a test token');
      }
    }

    // Validate email format
    if (emailData.sender && !this.isValidEmail(emailData.sender)) {
      errors.push('sender must be a valid email address');
    }

    // Validate content is not empty
    if (emailData.content && emailData.content.trim().length === 0) {
      errors.push('content cannot be empty');
    }

    // Check for production indicators
    if (emailData.access_token && emailData.access_token.startsWith('ya29.')) {
      console.log('✅ Valid Google OAuth access token detected');
    } else if (emailData.access_token) {
      console.log('⚠️ Access token format may be invalid');
    }

    const isValid = errors.length === 0;
    
    if (isValid) {
      console.log('✅ Production data validation passed');
    } else {
      console.log('❌ Production data validation failed:', errors);
    }
    
    return { isValid, errors };
  }

  /**
   * Validate email address format
   * @param {string} email - Email address to validate
   * @returns {boolean} True if valid email format
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Send request to N8N webhook with enhanced error handling
   * @param {Object} payload - Webhook payload
   * @returns {Promise<Response>} Fetch response
   */
  async sendWebhookRequest(payload) {
    const headers = {
      'Content-Type': 'application/json',
      'User-Agent': 'CampConnect/1.0.0'
    };

    // Use X-API-Key authentication for N8N webhook
    if (this.webhookToken) {
      headers['X-API-Key'] = this.webhookToken;
      console.log('🔑 Using X-API-Key authentication');
    } else if (this.webhookSecret) {
      // Fallback to webhook secret if token not available
      headers['X-Webhook-Secret'] = this.webhookSecret;
      console.log('🔑 Using X-Webhook-Secret authentication (fallback)');
    } else {
      console.warn('⚠️ No authentication token configured for webhook');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      console.log('📤 Sending webhook request:', {
        url: this.webhookUrl,
        testMode: payload.test_mode || false,
        processingType: payload.processing_type,
        userId: payload.user_id || payload.userId,
        messageId: payload.message_id || payload.messageId,
        authMethod: this.webhookToken ? 'X-API-Key' : 'X-Webhook-Secret'
      });

      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      
      // Enhanced response handling
      await this.handleWebhookResponse(response);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Enhanced error handling
      const enhancedError = this.enhanceWebhookError(error, payload);
      throw enhancedError;
    }
  }

  /**
   * Enhanced webhook response handling
   * @param {Response} response - Fetch response
   * @returns {Promise<void>}
   */
  async handleWebhookResponse(response) {
    const responseText = await response.text();
    
    console.log('📥 Webhook response:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
      body: responseText.substring(0, 200) + (responseText.length > 200 ? '...' : '')
    });

    if (!response.ok) {
      // Parse error response if possible
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        errorData = { message: responseText };
      }

      // Handle specific error types
      if (response.status === 401) {
        throw new Error(`Authentication failed: ${errorData.message || 'Invalid X-API-Key or webhook token'}`);
      } else if (response.status === 403) {
        throw new Error(`Access forbidden: ${errorData.message || 'Insufficient permissions or invalid token'}`);
      } else if (response.status === 404) {
        throw new Error(`Webhook not found: ${errorData.message || 'N8N webhook endpoint not found. Check webhook URL configuration.'}`);
      } else if (response.status === 400) {
        throw new Error(`Bad request: ${errorData.message || 'Invalid payload or missing required fields'}`);
      } else if (response.status === 429) {
        throw new Error(`Rate limit exceeded: ${errorData.message || 'Too many requests'}`);
      } else if (response.status === 500) {
        throw new Error(`Internal server error: ${errorData.message || 'N8N workflow server error. Check workflow configuration.'}`);
      } else if (response.status >= 500) {
        throw new Error(`Server error: ${errorData.message || 'N8N workflow server error'}`);
      } else {
        throw new Error(`Webhook request failed: ${response.status} ${response.statusText} - ${errorData.message || responseText}`);
      }
    }

    // Try to parse successful response
    try {
      const responseData = JSON.parse(responseText);
      console.log('✅ Webhook response parsed successfully:', {
        success: responseData.success !== false,
        messageId: responseData.messageId || responseData.message_id,
        processingTime: responseData.processingTime,
        extractedData: responseData.extractedData ? 'Present' : 'Not present'
      });
      return responseData;
    } catch (e) {
      console.log('⚠️ Webhook response is not JSON, treating as plain text');
      return { success: true, rawResponse: responseText };
    }
  }

  /**
   * Enhance webhook error with context
   * @param {Error} error - Original error
   * @param {Object} payload - Request payload for context
   * @returns {Error} Enhanced error
   */
  enhanceWebhookError(error, payload) {
    const enhancedError = new Error(error.message);
    enhancedError.originalError = error;
    enhancedError.payload = {
      testMode: payload.test_mode || false,
      processingType: payload.processing_type,
      userId: payload.user_id || payload.userId,
      messageId: payload.message_id || payload.messageId
    };
    enhancedError.timestamp = new Date().toISOString();
    
    // Add specific error context
    if (error.name === 'AbortError') {
      enhancedError.type = 'timeout';
      enhancedError.message = `Webhook request timed out after ${this.timeout}ms`;
    } else if (error.message.includes('Failed to fetch')) {
      enhancedError.type = 'network';
      enhancedError.message = 'Network error - check webhook URL and connectivity';
    } else if (error.message.includes('CORS')) {
      enhancedError.type = 'cors';
      enhancedError.message = 'CORS error - webhook server may not allow cross-origin requests';
    } else {
      enhancedError.type = 'unknown';
    }
    
    console.error('❌ Enhanced webhook error:', {
      type: enhancedError.type,
      message: enhancedError.message,
      payload: enhancedError.payload,
      timestamp: enhancedError.timestamp
    });
    
    return enhancedError;
  }

  /**
   * Handle webhook response
   * @param {Response} response - Fetch response
   * @returns {Promise<Object>} Processed result
   */
  async handleWebhookResponse(response) {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Webhook request failed: ${response.status} ${errorText}`);
    }

    try {
      const data = await response.json();
      return {
        success: true,
        messageId: data.messageId,
        processedData: data.processedData,
        processingTime: data.processingTime,
        webhookId: data.webhookId
      };
    } catch (error) {
      throw new Error('Invalid response format from webhook');
    }
  }

  /**
   * Retrieve real user data for production webhook requests
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User data with Gmail connection info
   */
  async getRealUserData(userId) {
    console.log('🔍 Retrieving real user data for production request:', userId);
    
    try {
      // Import supabase client
      const { supabase } = await import('@/lib/supabase');
      
      // Get user profile
      const { data: user, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (userError || !user) {
        throw new Error(`User not found: ${userError?.message || 'No user data'}`);
      }
      
      // Get Gmail connection metadata from JSONB helpers
      const emailConnections = await getEmailConnections(userId);
      const connectionMeta = emailConnections.find(conn => conn.provider === 'gmail');
      
      if (!connectionMeta) {
        throw new Error('Gmail connection not found');
      }
      
      // Get OAuth tokens from the old table (tokens must stay secure)
      const { data: tokenData, error: tokenError } = await supabase
        .from('user_email_connections')
        .select('access_token_encrypted, refresh_token_encrypted, id')
        .eq('id', connectionMeta.id)
        .single();
        
      if (tokenError || !tokenData) {
        throw new Error(`Failed to fetch OAuth tokens: ${tokenError?.message || 'No token data'}`);
      }
      
      // Combine metadata and tokens for processing
      const connection = {
        ...connectionMeta,
        ...tokenData
      };
      
      // Decrypt access token
      const emailService = await import('@/services/emailService');
      const decryptedAccessToken = emailService.default.decryptToken(connection.access_token);
      const decryptedRefreshToken = connection.refresh_token ? 
        emailService.default.decryptToken(connection.refresh_token) : null;
      
      console.log('✅ Real user data retrieved successfully:', {
        userId: user.id,
        email: user.email,
        hasAccessToken: !!decryptedAccessToken,
        hasRefreshToken: !!decryptedRefreshToken,
        connectionId: connection.id
      });
      
      return {
        user: {
          id: user.id,
          email: user.email,
          full_name: user.full_name,
          created_at: user.created_at
        },
        connection: {
          id: connection.id,
          provider: connection.provider,
          access_token: decryptedAccessToken,
          refresh_token: decryptedRefreshToken,
          expires_at: connection.expires_at,
          last_sync_date: connection.last_sync_date,
          processing_status: connection.processing_status
        }
      };
      
    } catch (error) {
      console.error('❌ Failed to retrieve real user data:', error);
      throw new Error(`Failed to retrieve user data: ${error.message}`);
    }
  }

  /**
   * Process email with real user data for production
   * @param {string} userId - User ID
   * @param {Object} emailData - Additional email data
   * @returns {Promise<Object>} Processing result
   */
  async processEmailWithRealUserData(userId, emailData = {}) {
    console.log('🚀 Processing email with real user data for production');
    
    try {
      // Get real user data
      const realUserData = await this.getRealUserData(userId);
      
      // Merge with provided email data
      const productionEmailData = {
        userId: userId,
        email_address: realUserData.user.email,
        access_token: realUserData.connection.access_token,
        refresh_token: realUserData.connection.refresh_token,
        processing_type: 'historical', // Default to historical processing
        ...emailData // Override with any provided data
      };
      
      // Validate and process
      const result = await this.processEmail(productionEmailData);
      
      console.log('✅ Production email processing completed:', {
        success: result.success,
        messageId: result.messageId,
        processingTime: result.processingTime
      });
      
      return result;
      
    } catch (error) {
      console.error('❌ Production email processing failed:', error);
      throw error;
    }
  }

  /**
   * Process multiple emails in batch with rate limiting
   * @param {Array} emailDataList - Array of email data
   * @returns {Promise<Array>} Array of processing results
   */
  async processBatchEmails(emailDataList) {
    const results = [];
    const batchDelay = parseInt(import.meta.env.VITE_BATCH_DELAY) || 2000; // 2 seconds between emails
    
    console.log(`📧 Processing batch of ${emailDataList.length} emails with ${batchDelay}ms delay between requests`);
    
    for (let i = 0; i < emailDataList.length; i++) {
      const emailData = emailDataList[i];
      
      try {
        console.log(`📧 Processing email ${i + 1}/${emailDataList.length}: ${emailData.messageId}`);
        const result = await this.processEmail(emailData);
        results.push({ ...result, emailId: emailData.messageId });
        
        // Add delay between emails (except for the last one)
        if (i < emailDataList.length - 1) {
          console.log(`⏳ Waiting ${batchDelay}ms before next email...`);
          await this.sleep(batchDelay);
        }
      } catch (error) {
        console.error(`❌ Failed to process email ${emailData.messageId}:`, error);
        results.push({
          success: false,
          emailId: emailData.messageId,
          error: error.message,
          attempt: 'failed'
        });
        
        // Still add delay even on failure to respect rate limits
        if (i < emailDataList.length - 1) {
          await this.sleep(batchDelay);
        }
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failureCount = results.length - successCount;
    
    console.log(`✅ Batch processing complete: ${successCount} successful, ${failureCount} failed`);
    
    return results;
  }

  /**
   * Test webhook connectivity with basic test data
   * @returns {Promise<boolean>} Connection status
   */
  async testConnection() {
    try {
      console.log('🧪 Testing webhook connection with test data...');
      
      const testPayload = {
        userId: 'test-user-123',
        messageId: 'test-message-456',
        subject: 'Test Connection',
        sender: 'test@example.com',
        content: 'Test webhook connection',
        rawMessage: 'Test',
        processing_type: 'test', // Explicit test mode
        metadata: { isTest: true, testType: 'connection' }
      };

      const result = await this.processEmail(testPayload);
      console.log('✅ Webhook connection test successful:', result);
      return true;
    } catch (error) {
      console.error('❌ Webhook connection test failed:', error);
      return false;
    }
  }

  /**
   * Test production webhook with real user data
   * @param {string} userId - Real user ID to test with
   * @returns {Promise<boolean>} Test result
   */
  async testProductionConnection(userId) {
    try {
      console.log('🚀 Testing production webhook with real user data:', userId);
      
      const result = await this.processEmailWithRealUserData(userId, {
        messageId: 'production-test-' + Date.now(),
        subject: 'Production Test Email',
        content: 'Testing production webhook with real user data'
      });
      
      console.log('✅ Production webhook test successful:', result);
      return true;
    } catch (error) {
      console.error('❌ Production webhook test failed:', error);
      return false;
    }
  }

  /**
   * Generate realistic Gmail test data for workflow testing
   * @param {Object} options - Test data options
   * @returns {Object} Realistic Gmail email data
   */
  generateRealisticGmailTestData(options = {}) {
    const {
      userId = 'user-12345',
      messageId = `gmail-message-${Date.now()}`,
      campType = 'summer-camp',
      senderDomain = 'gmail.com'
    } = options;

    // Sample camp-related email templates
    const emailTemplates = {
      'summer-camp': {
        subject: 'Summer Camp Registration Confirmation - Adventure Kids Camp',
        sender: `adventurecamps@gmail.com`,
        content: `Dear Parent,

Thank you for registering your child for Adventure Kids Summer Camp!

Camp Details:
- Dates: July 15-19, 2024
- Time: 9:00 AM - 3:00 PM
- Location: 123 Adventure Lane, Mountain View, CA
- Age Group: 8-12 years
- Cost: $350 per week

Your child is registered for:
- Swimming lessons
- Arts & crafts
- Outdoor adventures
- Team building activities

Please bring:
- Swimsuit and towel
- Sunscreen
- Water bottle
- Lunch (we provide healthy snacks)

Contact us at (555) 123-4567 if you have any questions.

Best regards,
The Adventure Kids Team`,
        rawMessage: `From: adventurecamps@gmail.com
To: parent@example.com
Subject: Summer Camp Registration Confirmation - Adventure Kids Camp
Date: Mon, 15 Jul 2024 10:30:00 -0700

Dear Parent,

Thank you for registering your child for Adventure Kids Summer Camp!

[Full email content as above...]`
      },
      'sports-camp': {
        subject: 'Soccer Camp Registration - Elite Sports Academy',
        sender: `elitesportsacademy@gmail.com`,
        content: `Hello!

Your registration for Elite Sports Soccer Camp has been confirmed.

Camp Information:
- Session: August 5-9, 2024
- Schedule: Monday-Friday, 8:00 AM - 4:00 PM
- Location: Elite Sports Complex, 456 Sports Ave, San Jose, CA
- Age Range: 10-14 years
- Fee: $280 per session

What to expect:
- Professional coaching
- Skill development sessions
- Scrimmages and tournaments
- Fitness training
- Team building exercises

Required equipment:
- Soccer cleats
- Shin guards
- Water bottle
- Athletic wear

Questions? Call us at (555) 987-6543

See you on the field!
Elite Sports Academy Staff`,
        rawMessage: `From: elitesportsacademy@gmail.com
To: parent@example.com
Subject: Soccer Camp Registration - Elite Sports Academy
Date: Tue, 16 Jul 2024 14:15:00 -0700

Hello!

Your registration for Elite Sports Soccer Camp has been confirmed.

[Full email content as above...]`
      },
      'art-camp': {
        subject: 'Creative Arts Camp - Registration Details',
        sender: `creativeartscamp@gmail.com`,
        content: `Dear Family,

Welcome to Creative Arts Camp! We're excited to have your child join us.

Camp Details:
- Dates: June 24-28, 2024
- Time: 10:00 AM - 2:00 PM
- Location: Art Studio Downtown, 789 Creative St, Palo Alto, CA
- Ages: 6-11 years
- Tuition: $200 per week

Activities include:
- Painting and drawing
- Pottery and ceramics
- Digital art
- Music and dance
- Theater and drama

Materials provided:
- All art supplies
- Aprons and smocks
- Healthy snacks

Please arrive 10 minutes early on the first day for orientation.

Contact: (555) 246-8135 or creativeartscamp@gmail.com

Artfully yours,
The Creative Arts Team`,
        rawMessage: `From: creativeartscamp@gmail.com
To: parent@example.com
Subject: Creative Arts Camp - Registration Details
Date: Wed, 17 Jul 2024 09:45:00 -0700

Dear Family,

Welcome to Creative Arts Camp! We're excited to have your child join us.

[Full email content as above...]`
      }
    };

    const template = emailTemplates[campType] || emailTemplates['summer-camp'];

    return {
      userId,
      messageId,
      subject: template.subject,
      sender: template.sender,
      content: template.content,
      rawMessage: template.rawMessage,
      timestamp: new Date().toISOString(),
      metadata: {
        isTest: true,
        testType: 'realistic-gmail',
        campType,
        gmailMessageId: messageId,
        gmailThreadId: `thread-${Date.now()}`,
        gmailLabels: ['INBOX', 'UNREAD', 'IMPORTANT'],
        gmailSnippet: template.content.substring(0, 100) + '...',
        source: 'gmail-api'
      }
    };
  }

  /**
   * Test N8N workflow with realistic Gmail data
   * @param {Object} options - Test options
   * @returns {Promise<Object>} Test result
   */
  async testWorkflowWithRealisticData(options = {}) {
    try {
      console.log('🧪 Starting realistic Gmail workflow test...');
      
      const testData = this.generateRealisticGmailTestData(options);
      console.log('📧 Generated test data:', {
        messageId: testData.messageId,
        subject: testData.subject,
        sender: testData.sender,
        campType: testData.metadata.campType
      });

      const result = await this.processEmail(testData);
      
      console.log('✅ Realistic workflow test completed successfully:', {
        success: result.success,
        messageId: result.messageId,
        processingTime: result.processingTime
      });

      return {
        success: true,
        testData,
        result,
        message: 'Realistic Gmail workflow test completed successfully'
      };
    } catch (error) {
      console.error('❌ Realistic workflow test failed:', error);
      return {
        success: false,
        error: error.message,
        message: 'Realistic Gmail workflow test failed'
      };
    }
  }

  /**
   * Test multiple camp types to verify workflow robustness
   * @returns {Promise<Array>} Array of test results
   */
  async testMultipleCampTypes() {
    console.log('🧪 Testing multiple camp types...');
    
    const campTypes = ['summer-camp', 'sports-camp', 'art-camp'];
    const results = [];
    
    for (const campType of campTypes) {
      console.log(`📧 Testing ${campType}...`);
      
      try {
        const result = await this.testWorkflowWithRealisticData({ campType });
        results.push({
          campType,
          success: result.success,
          message: result.message,
          error: result.error
        });
        
        // Add delay between tests to respect rate limits
        if (campType !== campTypes[campTypes.length - 1]) {
          await this.sleep(2000);
        }
      } catch (error) {
        results.push({
          campType,
          success: false,
          message: 'Test failed',
          error: error.message
        });
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Multiple camp type test completed: ${successCount}/${campTypes.length} successful`);
    
    return results;
  }

  /**
   * Test webhook with invalid data to verify error handling
   * @returns {Promise<Object>} Error handling test result
   */
  async testErrorHandling() {
    console.log('🧪 Testing error handling with invalid data...');
    
    const invalidDataTests = [
      {
        name: 'Missing required fields',
        data: { subject: 'Test' }, // Missing userId, messageId, etc.
        expectedError: 'Invalid email data'
      },
      {
        name: 'Invalid email format',
        data: {
          userId: 'test',
          messageId: 'test',
          subject: 'Test',
          sender: 'invalid-email',
          content: 'Test content'
        },
        expectedError: 'sender must be a valid email address'
      },
      {
        name: 'Wrong data types',
        data: {
          userId: 123, // Should be string
          messageId: 'test',
          subject: 'Test',
          sender: 'test@example.com',
          content: 'Test content'
        },
        expectedError: 'userId must be a string'
      }
    ];

    const results = [];
    
    for (const test of invalidDataTests) {
      try {
        console.log(`🔍 Testing: ${test.name}`);
        const payload = this.buildWebhookPayload(test.data);
        results.push({
          testName: test.name,
          success: false,
          message: 'Expected error was not thrown',
          error: 'No error occurred'
        });
      } catch (error) {
        const isExpectedError = error.message.includes(test.expectedError);
        results.push({
          testName: test.name,
          success: isExpectedError,
          message: isExpectedError ? 'Error handling working correctly' : 'Unexpected error type',
          error: error.message,
          expectedError: test.expectedError
        });
        console.log(`✅ ${test.name}: ${isExpectedError ? 'PASS' : 'FAIL'}`);
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Error handling test completed: ${successCount}/${invalidDataTests.length} passed`);
    
    return results;
  }

  /**
   * Get webhook configuration status
   * @returns {Object} Configuration status
   */
  getConfigStatus() {
    return {
      webhookUrl: !!this.webhookUrl,
      webhookSecret: !!this.webhookSecret,
      timeout: this.timeout,
      retryAttempts: this.retryAttempts,
      rateLimitDelay: this.rateLimitDelay,
      maxRetries: this.maxRetries,
      retryDelay: this.retryDelay,
      batchDelay: parseInt(import.meta.env.VITE_BATCH_DELAY) || 2000
    };
  }
}

export default new N8NWebhookService();
